export interface SessionTimelineThemeTokens {
  // 카드 스타일 (Liquid Glass)
  card: {
    background: string;
    border: string;
    borderRadius: string;
    padding: string;
    backdropFilter: string;
    WebkitBackdropFilter: string;
    boxShadow: string;
    boxShadowHover: string;
  };

  // 헤더 영역
  header: {
    icon: {
      size: string;
      color: string;
    };
    title: {
      fontSize: string;
      fontWeight: number | string;
      color: string;
    };
    indexBadge: {
      background: string;
      color: string;
      fontSize: string;
      padding: string;
      borderRadius: string;
      fontWeight?: number | string;
    };
    timestamp: {
      fontSize: string;
      color: string;
    };
    expandButton: {
      size: string;
      color: string;
    };
  };

  // 필드 영역
  field: {
    label: {
      fontSize: string;
      color: string;
      fontWeight: number | string;
    };
    value: {
      fontSize: string;
      color: string;
    };
    icon: {
      size: string;
      color: string;
    };
    spacing: string;
  };

  // 액션 버튼 영역
  action: {
    button: {
      fontSize: string;
      padding: string;
      borderRadius: string;
      minWidth: string;
      fontWeight?: number | string;
    };
    divider: {
      color: string;
      margin: string;
    };
    moreButton: {
      size: string;
      color: string;
    };
  };

  // 관련 이벤트 버튼
  relatedEvents: {
    button: {
      fontSize: string;
      color: string;
      padding: string;
      fontWeight?: number | string;
    };
    divider: {
      color: string;
      margin: string;
    };
  };

  // 펼치기 섹션 (세션 3개 이상일 때)
  expandSection: {
    button: { color: string; fontSize: string };
  };
}

import { colorPalette } from '@/shared/theme/tokens/colors';

// Light Theme Tokens
export const lightSessionTimelineTokens: SessionTimelineThemeTokens = {
  card: {
    background: 'rgba(255, 255, 255, 0.78)',
    border: '1px solid rgba(255, 255, 255, 0.65)',
    borderRadius: '16px',
    padding: '20px',
    backdropFilter: 'blur(18px) saturate(1.7)',
    WebkitBackdropFilter: 'blur(18px) saturate(1.7)',
    boxShadow: [
      '0 2px 12px rgba(0,0,0,0.08)',
      '0 1px 3px rgba(0,0,0,0.05)',
      `inset 0 1.5px 0 rgba(255,255,255,0.90)`,
      `inset 0 -1px 0 rgba(0,0,0,0.03)`,
    ].join(', '),
    boxShadowHover: [
      '0 6px 24px rgba(0,0,0,0.11)',
      '0 2px 6px rgba(0,0,0,0.07)',
      `inset 0 1.5px 0 rgba(255,255,255,0.90)`,
    ].join(', '),
  },
  header: {
    icon: {
      size: '28px',
      color: colorPalette.primary[500],
    },
    title: {
      fontSize: '17px',
      fontWeight: 600,
      color: colorPalette.neutral[900],
    },
    indexBadge: {
      background: colorPalette.primary[500],
      color: '#ffffff',
      fontSize: '11px',
      padding: '4px 10px',
      borderRadius: '16px',
      fontWeight: 600,
    },
    timestamp: {
      fontSize: '13px',
      color: colorPalette.neutral[500],
    },
    expandButton: {
      size: '20px',
      color: colorPalette.neutral[500],
    },
  },
  field: {
    label: {
      fontSize: '13px',
      color: colorPalette.neutral[500],
      fontWeight: 500,
    },
    value: {
      fontSize: '14px',
      color: colorPalette.neutral[900],
    },
    icon: {
      size: '20px',
      color: colorPalette.neutral[500],
    },
    spacing: '10px',
  },
  action: {
    button: {
      fontSize: '13px',
      padding: '8px 20px',
      borderRadius: '8px',
      minWidth: 'auto',
      fontWeight: 500,
    },
    divider: {
      color: colorPalette.neutral[200],
      margin: '16px 0',
    },
    moreButton: {
      size: '20px',
      color: colorPalette.neutral[500],
    },
  },
  relatedEvents: {
    button: {
      fontSize: '13px',
      color: colorPalette.primary[500],
      padding: '10px 0',
      fontWeight: 500,
    },
    divider: {
      color: colorPalette.neutral[200],
      margin: '16px 0',
    },
  },
  expandSection: {
    button: { color: colorPalette.primary[500], fontSize: '13px' },
  },
};

// Dark Theme Tokens
export const darkSessionTimelineTokens: SessionTimelineThemeTokens = {
  card: {
    background: 'rgba(26, 26, 42, 0.72)',
    border: '1px solid rgba(255, 255, 255, 0.09)',
    borderRadius: '16px',
    padding: '20px',
    backdropFilter: 'blur(18px) saturate(1.5)',
    WebkitBackdropFilter: 'blur(18px) saturate(1.5)',
    boxShadow: [
      '0 2px 12px rgba(0,0,0,0.32)',
      '0 1px 3px rgba(0,0,0,0.20)',
      'inset 0 1px 0 rgba(255,255,255,0.07)',
      'inset 0 -1px 0 rgba(0,0,0,0.20)',
    ].join(', '),
    boxShadowHover: [
      '0 6px 24px rgba(0,0,0,0.40)',
      '0 2px 6px rgba(0,0,0,0.24)',
      'inset 0 1px 0 rgba(255,255,255,0.09)',
    ].join(', '),
  },
  header: {
    icon: {
      size: '28px',
      color: colorPalette.primary[400],
    },
    title: {
      fontSize: '17px',
      fontWeight: 600,
      color: colorPalette.neutralDark[900],
    },
    indexBadge: {
      background: colorPalette.primary[500],
      color: '#ffffff',
      fontSize: '11px',
      padding: '4px 10px',
      borderRadius: '16px',
      fontWeight: 600,
    },
    timestamp: {
      fontSize: '13px',
      color: colorPalette.neutralDark[600],
    },
    expandButton: {
      size: '20px',
      color: colorPalette.neutralDark[600],
    },
  },
  field: {
    label: {
      fontSize: '13px',
      color: colorPalette.neutralDark[600],
      fontWeight: 500,
    },
    value: {
      fontSize: '14px',
      color: colorPalette.neutralDark[900],
    },
    icon: {
      size: '20px',
      color: colorPalette.neutralDark[600],
    },
    spacing: '10px',
  },
  action: {
    button: {
      fontSize: '13px',
      padding: '8px 20px',
      borderRadius: '8px',
      minWidth: 'auto',
      fontWeight: 500,
    },
    divider: {
      color: colorPalette.neutralDark[400],
      margin: '16px 0',
    },
    moreButton: {
      size: '20px',
      color: colorPalette.neutralDark[600],
    },
  },
  relatedEvents: {
    button: {
      fontSize: '13px',
      color: colorPalette.primary[400],
      padding: '10px 0',
      fontWeight: 500,
    },
    divider: {
      color: colorPalette.neutralDark[400],
      margin: '16px 0',
    },
  },
  expandSection: {
    button: { color: colorPalette.primary[400], fontSize: '13px' },
  },
};
